(() => {
  'use strict';

  const START_PATH = '/ec/access/start';
  const MAX_ITEMS = 20;
  const MAX_QUANTITY = 99;
  const PRODUCT_GID_PATTERN = /^gid:\/\/shopify\/Product\/([1-9][0-9]*)$/u;
  const VARIANT_GID_PATTERN = /^gid:\/\/shopify\/ProductVariant\/([1-9][0-9]*)$/u;
  const ALLOWED_MATCHING_ORIGINS = Object.freeze(['https://matching.keysapo.com']);
  const INTENT_KEYS = Object.freeze(['productGid', 'quantity', 'variantGid']);

  const isHandoffEnabled = (value) => value === 'true';

  const asShopifyGid = (value, type) => {
    const stringValue = String(value ?? '').trim();
    const pattern = type === 'Product' ? PRODUCT_GID_PATTERN : VARIANT_GID_PATTERN;
    if (pattern.test(stringValue)) return stringValue;
    return /^[1-9][0-9]*$/u.test(stringValue) ? `gid://shopify/${type}/${stringValue}` : '';
  };

  const normalizeQuantity = (value) => {
    const stringValue = String(value ?? '').trim();
    if (!/^[0-9]+$/u.test(stringValue)) return null;
    const quantity = Number(stringValue);
    return Number.isSafeInteger(quantity) && quantity >= 1 && quantity <= MAX_QUANTITY ? quantity : null;
  };

  const normalizeQuantityInput = ({ value, min = '', max = '', step = '', valid = true }) => {
    const quantity = normalizeQuantity(value);
    if (quantity === null || valid !== true) return null;

    const parseRule = (rule, fallback) => {
      const stringValue = String(rule ?? '').trim();
      if (stringValue === '') return fallback;
      if (!/^[0-9]+$/u.test(stringValue)) return null;
      const parsed = Number(stringValue);
      return Number.isSafeInteger(parsed) && parsed >= 1 ? parsed : null;
    };
    const minimum = parseRule(min, 1);
    const maximum = parseRule(max, MAX_QUANTITY);
    const increment = String(step ?? '').trim() === 'any' ? 1 : parseRule(step, 1);
    if (minimum === null || maximum === null || increment === null || minimum > maximum) return null;
    if (quantity < minimum || quantity > Math.min(maximum, MAX_QUANTITY)) return null;
    return (quantity - minimum) % increment === 0 ? quantity : null;
  };

  const normalizeIntentItem = (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    if (Object.keys(value).sort().join('\u0000') !== INTENT_KEYS.join('\u0000')) return null;

    const productGid = asShopifyGid(value.productGid, 'Product');
    const variantGid = asShopifyGid(value.variantGid, 'ProductVariant');
    const quantity = normalizeQuantity(value.quantity);
    return productGid && variantGid && quantity !== null
      ? { productGid, variantGid, quantity }
      : null;
  };

  const normalizeIntentItems = (values) => {
    if (!Array.isArray(values) || values.length < 1 || values.length > MAX_ITEMS) {
      throw new Error('見積対象の商品数を確認できませんでした。');
    }

    const seenVariants = new Set();
    return values.map((value) => {
      const item = normalizeIntentItem(value);
      if (!item || seenVariants.has(item.variantGid)) {
        throw new Error('見積対象の商品情報を確認できませんでした。');
      }
      seenVariants.add(item.variantGid);
      return item;
    });
  };

  const encodeBase64Url = (value) => {
    const json = JSON.stringify(value);
    if (typeof TextEncoder !== 'undefined' && typeof btoa === 'function') {
      const bytes = new TextEncoder().encode(json);
      let binary = '';
      bytes.forEach((byte) => {
        binary += String.fromCharCode(byte);
      });
      return btoa(binary).replace(/\+/gu, '-').replace(/\//gu, '_').replace(/=+$/u, '');
    }
    if (typeof Buffer !== 'undefined') return Buffer.from(json, 'utf8').toString('base64url');
    throw new Error('見積データを安全に準備できませんでした。');
  };

  const resolveMatchingOrigin = (value) => {
    try {
      const url = new URL(String(value ?? '').trim());
      const isBareOrigin =
        url.username === '' &&
        url.password === '' &&
        url.pathname === '/' &&
        url.search === '' &&
        url.hash === '';
      return isBareOrigin && ALLOWED_MATCHING_ORIGINS.includes(url.origin) ? url.origin : '';
    } catch {
      return '';
    }
  };

  const buildHandoffUrl = ({ matchingOrigin, items }) => {
    const origin = resolveMatchingOrigin(matchingOrigin);
    if (!origin) throw new Error('見積相談の接続先が許可されていません。');

    const normalizedItems = normalizeIntentItems(items);
    const url = new URL(START_PATH, origin);
    url.hash = `v=1&items=${encodeBase64Url(normalizedItems)}`;
    return url.toString();
  };

  const readSelectedVariantGid = (element) => {
    const productInfo = element.closest('product-info');
    const variantSelects = productInfo?.querySelector('variant-selects');
    if (!variantSelects) {
      return asShopifyGid(element.dataset.initialVariantGid, 'ProductVariant');
    }
    try {
      const selected = JSON.parse(variantSelects.querySelector('[data-selected-variant]')?.textContent || 'null');
      return asShopifyGid(selected?.id, 'ProductVariant');
    } catch {
      return '';
    }
  };

  const readQuantity = (element) => {
    const input = element.querySelector('[data-keysapo-quote-quantity]');
    if (!input) return null;
    return normalizeQuantityInput({
      value: input.value,
      min: input.min,
      max: input.max,
      step: input.step,
      valid: input.checkValidity?.() ?? true,
    });
  };

  const exported = Object.freeze({
    ALLOWED_MATCHING_ORIGINS,
    MAX_ITEMS,
    MAX_QUANTITY,
    START_PATH,
    asShopifyGid,
    buildHandoffUrl,
    encodeBase64Url,
    isHandoffEnabled,
    normalizeIntentItem,
    normalizeIntentItems,
    normalizeQuantity,
    normalizeQuantityInput,
    readQuantity,
    readSelectedVariantGid,
    resolveMatchingOrigin,
  });

  if (typeof module !== 'undefined' && module.exports) module.exports = exported;
  if (typeof window === 'undefined' || typeof customElements === 'undefined' || typeof HTMLElement === 'undefined') {
    return;
  }

  if (!customElements.get('keysapo-quote-handoff')) {
    customElements.define(
      'keysapo-quote-handoff',
      class KeysapoQuoteHandoff extends HTMLElement {
        connectedCallback() {
          this.button = this.querySelector('[data-keysapo-quote-start]');
          this.quantityInput = this.querySelector('[data-keysapo-quote-quantity]');
          this.status = this.querySelector('[data-keysapo-quote-status]');
          if (!this.button) return;
          if (!this.quantityInput) {
            this.button.disabled = true;
            return;
          }
          this.onStart = this.handleStart.bind(this);
          this.onPageShow = this.handlePageShow.bind(this);
          this.onQuantityInput = this.handleQuantityInput.bind(this);
          this.onOptionValueSelectionChange = this.handleOptionValueSelectionChange.bind(this);
          this.onVariantChange = this.handleVariantChange.bind(this);
          this.button.addEventListener('click', this.onStart);
          this.quantityInput.addEventListener('input', this.onQuantityInput);
          window.addEventListener('pageshow', this.onPageShow);
          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
            this.optionValueSelectionChangeUnsubscriber = subscribe(
              PUB_SUB_EVENTS.optionValueSelectionChange,
              this.onOptionValueSelectionChange
            );
            this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
          }
          this.variantPending = false;
          this.syncButtonState();
        }

        disconnectedCallback() {
          this.button?.removeEventListener('click', this.onStart);
          this.quantityInput?.removeEventListener('input', this.onQuantityInput);
          window.removeEventListener('pageshow', this.onPageShow);
          this.optionValueSelectionChangeUnsubscriber?.();
          this.variantChangeUnsubscriber?.();
        }

        syncButtonState() {
          if (!this.button) return false;
          const ready =
            isHandoffEnabled(this.dataset.enabled) &&
            this.variantPending !== true &&
            readSelectedVariantGid(this) !== '' &&
            readQuantity(this) !== null;
          this.button.disabled = !ready;
          if (!ready) this.button.removeAttribute('aria-busy');
          return ready;
        }

        handleOptionValueSelectionChange(payload) {
          const productInfo = this.closest('product-info');
          if (!productInfo?.contains(payload?.data?.event?.target)) return;
          this.variantPending = true;
          this.syncButtonState();
          this.setStatus('選択した仕様を確認しています。利用できない組み合わせでは見積相談を開始できません。');
        }

        handleQuantityInput() {
          if (this.syncButtonState()) {
            this.setStatus('');
          } else if (this.variantPending !== true && readSelectedVariantGid(this) !== '') {
            this.setStatus(`数量条件を確認してください（全体上限${MAX_QUANTITY}）。`, true);
          }
        }

        syncQuantityRules(html) {
          const updated = html?.getElementById(this.id)?.querySelector('[data-keysapo-quote-quantity]');
          const help = this.querySelector('[data-keysapo-quote-quantity-help]');
          const updatedHelp = html
            ?.getElementById(this.id)
            ?.querySelector('[data-keysapo-quote-quantity-help]');
          if (!this.quantityInput || !updated || !help || !updatedHelp) return false;
          for (const attribute of ['min', 'max', 'step', 'value']) {
            const value = updated.getAttribute(attribute);
            if (value === null) {
              this.quantityInput.removeAttribute(attribute);
            } else {
              this.quantityInput.setAttribute(attribute, value);
            }
          }
          this.quantityInput.value = updated.value;
          help.textContent = updatedHelp.textContent;
          return true;
        }

        handleVariantChange(payload) {
          const productInfo = this.closest('product-info');
          const sectionId = productInfo?.dataset.originalSection || productInfo?.dataset.section;
          if (!sectionId || payload?.data?.sectionId !== sectionId) return;
          if (!this.syncQuantityRules(payload?.data?.html)) {
            this.variantPending = true;
            this.syncButtonState();
            this.setStatus('選択した仕様の数量条件を確認できませんでした。', true);
            return;
          }
          this.variantPending = false;
          if (this.syncButtonState()) {
            this.setStatus('');
          } else {
            this.setStatus('選択した仕様では見積相談を開始できません。', true);
          }
        }

        handlePageShow() {
          if (!this.syncButtonState()) return;
          this.button.removeAttribute('aria-busy');
          this.setStatus('');
        }

        setStatus(message, isError = false) {
          if (!this.status) return;
          this.status.textContent = message;
          this.status.setAttribute('role', isError ? 'alert' : 'status');
        }

        handleStart() {
          if (!isHandoffEnabled(this.dataset.enabled)) {
            this.setStatus('見積相談機能は現在準備中です。', true);
            return;
          }
          if (this.variantPending === true) {
            this.setStatus('選択した仕様を確認しています。しばらくお待ちください。', true);
            return;
          }

          const quantity = readQuantity(this);
          const variantGid = readSelectedVariantGid(this);
          if (quantity === null) {
            this.syncButtonState();
            this.setStatus(`数量の最小値・最大値・入力単位を確認してください（全体上限${MAX_QUANTITY}）。`, true);
            return;
          }
          if (!variantGid) {
            this.syncButtonState();
            this.setStatus('選択した仕様では見積相談を開始できません。', true);
            return;
          }

          try {
            const url = buildHandoffUrl({
              matchingOrigin: this.dataset.matchingOrigin,
              items: [
                {
                  productGid: this.dataset.productGid,
                  variantGid,
                  quantity,
                },
              ],
            });
            this.button.disabled = true;
            this.button.setAttribute('aria-busy', 'true');
            this.setStatus('マッチングサイトで会員資格を確認します。');
            this.navigateToHandoff(url);
          } catch (error) {
            this.setStatus(error instanceof Error ? error.message : '見積相談を開始できませんでした。', true);
          }
        }

        navigateToHandoff(url) {
          window.location.assign(url);
        }
      }
    );
  }
})();
